/**
 * src/lib/capture/useCaptureRouter.js — the WikiLLM file router, as a hook.
 *
 * Extracted from VoiceFileUploadBtn (TICKET-912 slice 4) so the chat's attach
 * button files uploads through the SAME proven routes instead of a fork:
 *   image                 -> photos pipeline (enqueue photos.create), scoped
 *   audio                 -> direct MinIO upload -> UVTP (global upload tray)
 *   contact card (.vcf)   -> parsed on the device -> directory contacts (TICKET-926)
 *   text / markdown       -> captureVoice as a pre-transcribed note
 *   PDF / Office / CSV / CAD -> the vault (UploadFilesPanel: classify -> scope confirm -> file)
 *   anything else         -> a visible "routing coming" card, never a silent drop
 * An image with no photo scope (no deal/project/vessel, e.g. the general chat)
 * goes to the vault too, whose confirm sheet picks the anchor.
 *
 * The host renders `uploads` (ProgressCard) and, when `vaultQueue` is set,
 * <UploadFilesPanel initialFiles={vaultQueue} context={vaultContext} />.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchUploadProgress } from '../api/voice-v2';
import { useT } from '../i18n/I18nContext';
import { isAcceptableAudioFile } from '../voice/isAcceptableAudioFile';
import { upsertUploadJob, patchUploadJob } from '../voice/uploadStatusStore';
import { sha256Hex } from '../api/photos';
import { enqueue } from '../offline/outbox';
import { syncNow } from '../offline/syncEngine';
import { isCapturableFile } from './captureAndFile';
import { isVcardFile } from './vcard';
import { isCadFileName } from '../../../lib/files/cadFiles.mjs';
import { logAppError } from '../errorReporter';

// contextType -> the vault scope key. task/bundle have no document anchor of
// their own, so those drops fall through to the confirm sheet's own picker.
const VAULT_SCOPE_KEY = { project: 'project_id', deal: 'deal_id', vessel: 'vessel_id' };

export default function useCaptureRouter({ contextType = 'project', contextId, onUpload } = {}) {
  const { t } = useT();
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]); // [{ uploadId, fileName, status, chunksDone, chunksTotal, headline, error }]
  // Documents (PDF/Office/CSV) handed to the WikiLLM vault panel in a sheet.
  const [vaultQueue, setVaultQueue] = useState(null);
  const pollTimers = useRef({});

  const scopeKey = VAULT_SCOPE_KEY[contextType];
  const vaultContext = scopeKey && contextId ? { [scopeKey]: contextId } : {};

  // Cleanup poll timers on unmount
  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      Object.values(timers).forEach(clearInterval);
    };
  }, []);

  const startPolling = useCallback((uploadId) => {
    const interval = setInterval(async () => {
      try {
        const progress = await fetchUploadProgress(uploadId);
        setUploads(prev => prev.map(u =>
          u.uploadId === uploadId
            ? { ...u, status: progress.status, chunksDone: progress.chunks_done, chunksTotal: progress.chunks_total, headline: progress.headline, error: progress.error }
            : u
        ));

        // Stop polling when done or errored
        if (['ready', 'classified', 'error'].includes(progress.status)) {
          clearInterval(pollTimers.current[uploadId]);
          delete pollTimers.current[uploadId];

          // Auto-dismiss done items after 10s
          if (progress.status === 'ready' || progress.status === 'classified') {
            setTimeout(() => {
              setUploads(prev => prev.filter(u => u.uploadId !== uploadId));
            }, 10000);
          }
        }
      } catch { // spine-emit-ok: transient poll miss; the next 3s tick retries
        // Polling error — ignore, will retry
      }
    }, 3000);

    pollTimers.current[uploadId] = interval;
  }, []);

  const dismissUpload = useCallback((uploadId) => {
    if (pollTimers.current[uploadId]) {
      clearInterval(pollTimers.current[uploadId]);
      delete pollTimers.current[uploadId];
    }
    setUploads(prev => prev.filter(u => u.uploadId !== uploadId));
  }, []);

  // wikillm router: an IMAGE goes to the PROVEN photo pipeline (enqueue
  // photos.create -> photos.register, UNGATED), scoped from the button context.
  const processImageFile = useCallback(async (file) => {
    const scope = contextType === 'project' ? { project_id: contextId }
      : contextType === 'deal' ? { deal_id: contextId }
      : contextType === 'vessel' ? { vessel_id: contextId } : null;
    if (!scope) return;

    const tempId = `img_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setUploads(prev => [...prev, {
      uploadId: tempId, fileName: file.name, status: 'uploading',
      chunksDone: 0, chunksTotal: 0, headline: null, error: null,
    }]);

    try {
      const content_sha256 = await sha256Hex(file);
      const client_id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID() : `cap-${Date.now()}`;
      await enqueue({
        domain: 'photos', type: 'create', entity_id: client_id,
        payload: {
          blob: file, content_sha256, ...scope, product_types: [],
          file_name: file.name, file_size: file.size || 0,
          mime_type: file.type || 'image/jpeg',
        },
      });
      syncNow().catch(() => { /* spine-emit-ok: the outbox keeps the item and the sync engine retries */ });
      setUploads(prev => prev.map(u =>
        u.uploadId === tempId ? { ...u, status: 'ready', headline: file.name } : u
      ));
      // Auto-dismiss after 8s
      setTimeout(() => setUploads(prev => prev.filter(u => u.uploadId !== tempId)), 8000);
      if (onUpload) onUpload();
    } catch (err) {
      console.error('[VoiceUpload] image error:', err);
      logAppError({ type: 'capture_error', message: err?.message, view: 'useCaptureRouter', metadata: { step: 'capture.image', context_type: contextType } });
      setUploads(prev => prev.map(u =>
        u.uploadId === tempId ? { ...u, status: 'error', error: err.message } : u
      ));
    }
  }, [contextType, contextId, onUpload]);

  // TICKET-926: a contact card becomes contacts (vcardCapture.js — parsed on the
  // device, created through create_vessel_contact, deduped server-side). Checked
  // BEFORE the text branch: text/vcard used to be filed as a note. Business-card
  // PHOTOS are deferred (they need a guarded vision call) and still file as photos.
  const processVcard = useCallback(async (file) => {
    const tempId = `vcf_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setUploads(prev => [...prev, { uploadId: tempId, fileName: file.name, status: 'uploading', chunksDone: 0, chunksTotal: 0, headline: null, error: null }]);
    try {
      const { captureVcardFile } = await import('./vcardCapture');
      const r = await captureVcardFile(file, { contextType, contextId });
      if (r.failed || !(r.created + r.existing)) {
        const why = r.failed ? r.errors.join('; ') : 'no named contact in the card';
        logAppError({ type: 'capture_error', message: why, view: 'useCaptureRouter', metadata: { step: 'capture.vcard', context_type: contextType, cards: r.cards, failed: r.failed } });
        setUploads(prev => prev.map(u => (u.uploadId === tempId ? { ...u, status: 'error', error: t('forms.vcardNotSaved') } : u)));
        return;
      }
      setUploads(prev => prev.map(u => (u.uploadId === tempId
        ? { ...u, status: 'ready', headline: t('forms.vcardContactsSaved', { count: r.created + r.existing }) } : u)));
      setTimeout(() => setUploads(prev => prev.filter(u => u.uploadId !== tempId)), 8000);
      if (onUpload) onUpload();
    } catch (err) {
      logAppError({ type: 'capture_error', message: err?.message, view: 'useCaptureRouter', metadata: { step: 'capture.vcard', context_type: contextType } });
      setUploads(prev => prev.map(u => (u.uploadId === tempId ? { ...u, status: 'error', error: err.message } : u)));
    }
  }, [contextType, contextId, onUpload, t]);

  // TICKET-1130: returns the outcome ({ok:true} | {ok:false, error}) so a host that
  // shows per-file status (UploadFilesPanel) never says "Filed" for a failure.
  const processFile = useCallback(async (file) => {
    if (file.size > 500 * 1024 * 1024) return { ok: false, error: 'too_large' };

    const isText = file.name.endsWith('.md') || file.name.endsWith('.txt') || file.type.startsWith('text/');
    // CEO bug 2026-05-03: iOS Voice Memos arrive with file.type="" or
    // 'audio/x-m4a' or 'application/octet-stream'. Helper checks mime OR
    // extension. See lib/voice/isAcceptableAudioFile.js.
    const isAudio = isAcceptableAudioFile(file);
    if (!isText && !isAudio) return { ok: false, error: 'unsupported_mime' };

    if (isText) {
      // Text/markdown: read content, send as pre-transcribed text -> classify
      // directly. Stays on the local ProgressCard (instant flow, no upload phase,
      // so there is nothing for the global upload tray to track).
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      setUploads(prev => [...prev, { uploadId: tempId, fileName: file.name, status: 'uploading', chunksDone: 0, chunksTotal: 0, headline: null, error: null }]);
      try {
        const text = await file.text();
        const { captureVoice } = await import('../api/voice-v2');
        const result = await captureVoice({
          audio: null,
          transcript: text,
          contextType,
          contextId,
          contextHint: 'text_import',
          idempotencyKey: `text-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          fileName: file.name,
        });
        setUploads(prev => prev.map(u =>
          u.uploadId === tempId
            ? { ...u, uploadId: result?.voice_record_id || tempId, status: result?.status === 'ready' ? 'ready' : 'processing', headline: file.name }
            : u
        ));
        if (result?.voice_record_id) startPolling(result.voice_record_id);
        if (onUpload) onUpload();
      } catch (err) {
        console.error('[VoiceUpload] text error:', err);
        logAppError({ type: 'capture_error', message: err?.message, view: 'useCaptureRouter', metadata: { step: 'capture.text', context_type: contextType } });
        setUploads(prev => prev.map(u =>
          u.uploadId === tempId ? { ...u, status: 'error', error: err.message } : u
        ));
        return { ok: false, error: err?.message };
      }
      return { ok: true };
    }

    // DIRECT UPLOAD (TICKET-MINIO-DIRECT-UPLOAD + TICKET-UPLOAD-PROGRESS-UX-T1) — audio.
    // The picked File streams STRAIGHT to MinIO with NO IndexedDB round-trip -- iOS
    // Safari cannot reliably hold/read an 80-200 MB blob in IDB (the old IDB-queue
    // path stranded big iPhone uploads at "waiting for worker"). Status is surfaced
    // in the GLOBAL VoiceQueueTray via uploadStatusStore (NOT a local card), so the
    // upload has a persistent lifecycle cue (uploading% -> uploaded -> transcribing
    // -> ready) that survives navigating away from this button (CEO 2026-06-02:
    // "you upload a file and have no visual cue of what's going on"). recording_id
    // is generated here and threaded as the idempotency_key end-to-end
    // (voice_uploads -> voice_records) AND is the store job id.
    // uploadFileWithBreadcrumb still emits the optimistic breadcrumb for
    // voice_records reconciliation. The IDB queue stays for live mic chunks.
    const recordingId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `rec-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    upsertUploadJob({
      id: recordingId,
      fileName: file.name,
      phase: 'uploading',
      pct: 0,
      scopeType: contextType,
      scopeId: contextId,
      createdAt: Date.now(),
    });
    try {
      const { uploadFileWithBreadcrumb } = await import('../voice/uploadFileWithBreadcrumb');
      // Throttle pct -> store writes to ~400ms (and always the final 100%). Each
      // store emit re-renders the tray, so a per-% write storm would re-introduce
      // the iOS "screen vibrates each %" bug (CEO 2026-06-02). 400ms is smooth
      // without flicker; the <progress> bar steps coarsely between writes.
      let lastEmit = 0;
      const { result } = await uploadFileWithBreadcrumb({
        file,
        contextType,
        contextId,
        recording_id: recordingId,
        onProgress: (sent, total) => {
          const pct = total ? Math.round((sent / total) * 100) : 0;
          const now = Date.now();
          if (pct < 100 && now - lastEmit < 400) return;
          lastEmit = now;
          patchUploadJob(recordingId, { pct });
        },
      });
      // Upload + register done -- hand off to the tray's post-upload poller
      // (useUploadJobs polls voice.upload_progress on uploadId -> ready).
      patchUploadJob(recordingId, { phase: 'uploaded', uploadId: result?.upload_id ?? null });
      if (onUpload) onUpload();
      return { ok: true };
    } catch (err) {
      console.error('[VoiceUpload] error:', err);
      logAppError({ type: 'capture_error', message: err?.message, view: 'useCaptureRouter', metadata: { step: 'capture.audio', context_type: contextType } });
      patchUploadJob(recordingId, { phase: 'error', error: err.message });
      return { ok: false, error: err?.message };
    }
  }, [contextId, contextType, onUpload, startPolling]);

  // Resolves to the audio/text outcomes [{file, ok, error?}] (TICKET-1130) — additive:
  // callers that ignore the return value are unaffected.
  const processFiles = useCallback(async (fileList) => {
    const all = Array.from(fileList || []);
    const results = [];
    if (all.length === 0) return results;
    const vcards = all.filter(isVcardFile);
    const files = all.filter(f => !vcards.includes(f));

    // WikiLLM router: split files by type BEFORE any size-gate so every file
    // gets a visible card — nothing is silently dropped (CEO directive 2026-06-18).
    // TICKET-908: .dxf/.dwg can report image/vnd.* — drawings go to the vault, not photos.
    // An image with no photo scope (general chat, task, bundle) goes to the vault,
    // whose confirm sheet picks the anchor, instead of being dropped.
    const photoScoped = !!(scopeKey && contextId);
    const images = files.filter(f => (f.type || '').startsWith('image/') && !isCadFileName(f.name));
    const photoImages = photoScoped ? images : [];
    const audioOrText = files.filter(f => !isCadFileName(f.name) && (
      isAcceptableAudioFile(f) || f.type.startsWith('text/') ||
      f.name.endsWith('.md') || f.name.endsWith('.txt')
    ));
    // Everything that is neither image nor audio/text — PDFs (a boat's schematics
    // and drawings), Office docs, CSVs. These used to render a red "routing
    // coming" card and go nowhere, which is why a PDF could not be uploaded from
    // a deal at all (TICKET-459). The WikiLLM vault has accepted these MIME types
    // since slice E and its server flag is live, so hand them to the SAME capture
    // pipeline (classify -> scope confirm -> register) instead of refusing them.
    const others = files.filter(f => !photoImages.includes(f) && !audioOrText.includes(f));
    const vaultFiles = others.filter(isCapturableFile);
    if (vaultFiles.length) setVaultQueue(vaultFiles);

    // Anything the vault does not accept either still gets a visible card —
    // never a silent failure.
    for (const f of others.filter(f => !isCapturableFile(f))) {
      const uid = `unsup_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      setUploads(prev => [...prev, {
        uploadId: uid, fileName: f.name, status: 'error',
        error: t('forms.uploadRoutingComing'),
        chunksDone: 0, chunksTotal: 0, headline: null,
      }]);
      setTimeout(() => setUploads(prev => prev.filter(u => u.uploadId !== uid)), 8000);
    }

    const oversized = audioOrText.filter(f => f.size > 500 * 1024 * 1024);
    if (oversized.length > 0) {
      // Show oversized error as a temporary upload entry instead of a browser dialog
      const errorId = `oversized_${Date.now()}`;
      setUploads(prev => [...prev, { uploadId: errorId, fileName: `${oversized.length} file(s)`, status: 'error', error: t('forms.tooLarge'), chunksDone: 0, chunksTotal: 0, headline: null }]);
      setTimeout(() => setUploads(prev => prev.filter(u => u.uploadId !== errorId)), 5000);
    }
    for (const file of oversized) results.push({ file, ok: false, error: 'too_large' });
    const validAudioText = audioOrText.filter(f => f.size <= 500 * 1024 * 1024);

    if (photoImages.length === 0 && validAudioText.length === 0 && vcards.length === 0) return results;

    setUploading(true);

    // Process files sequentially to avoid overwhelming the API.
    // try/finally is REQUIRED — if processFile throws, the previous code left
    // setUploading(true) in place and the button stayed permanently grey until
    // page reload. (Reported during UAT 2026-04-07 PM.)
    try {
      for (const file of vcards) await processVcard(file);
      for (const file of photoImages) {
        try {
          await processImageFile(file);
        } catch (perFileErr) { // spine-emit-ok: processImageFile emits its own capture_error
          console.warn('[voice-upload] processImageFile error:', perFileErr?.message);
        }
      }
      for (const file of validAudioText) {
        try {
          const outcome = await processFile(file);
          results.push({ file, ok: !!outcome?.ok, ...(outcome?.ok ? {} : { error: outcome?.error || 'upload_failed' }) });
        } catch (perFileErr) { // spine-emit-ok: processFile emits its own capture_error
          console.warn('[voice-upload] processFile error:', perFileErr?.message);
          results.push({ file, ok: false, error: perFileErr?.message || 'upload_failed' });
          // Continue to next file rather than breaking out and stranding state
        }
      }
    } finally {
      setUploading(false);
    }
    return results;
  }, [processImageFile, processFile, processVcard, t, scopeKey, contextId]);

  const closeVault = useCallback(() => setVaultQueue(null), []);

  return { uploading, uploads, dismissUpload, processFiles, vaultQueue, vaultContext, closeVault };
}
